import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { style: {"display":"flex"} }

import CmTable from '@/components/cmTable/index.vue'
import CmForm from '@/components/cmForm/index.vue'
import { nextTick, reactive, ref, computed } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  FormActionType,
  FormSize,
  FormType,
  ObjectFormItem,
} from '@/components/cmForm/types'
import { TableActOpt, TableOpt } from '@/components/cmTable/types'
import { formatTimeStamp } from '@/utils/time'
import { InviteState, InviteActionType, InviteListService, InviteItem, InviteListReq } from './invite-list.service'
import { formatMapToOptions } from '@/utils/tool'
import { ElMessage, ElMessageBox } from 'element-plus'
import InviteLink from './_components/invite-link/invite-link.vue'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import CreateAccount from '@/views/dashboard/_components/create-account/create-account.vue'
import {
  CreateAccountService,
  InviteEmitData
} from '@/views/dashboard/_components/create-account/create-account.service'
import { listCol } from '@/utils/list-columns-i18n'


export default /*#__PURE__*/_defineComponent({
  __name: 'invite-list',
  emits: ['refresh'],
  setup(__props, { emit: __emit }) {

const { t, locale } = useI18n()
const emits = __emit

const inviteStateKeyMap: Record<InviteState, string> = {
  [InviteState.pending]: 'statePending',
  [InviteState.allowed]: 'stateAllowed',
  [InviteState.rejected]: 'stateRejected',
  [InviteState.canceled]: 'stateCanceled',
  [InviteState.expire]: 'stateExpire',
}
const injector = useReflectiveInjector([InviteListService, CreateAccountService])
const inviteListService = injector.get(InviteListService)
const createAccountService = injector.get(CreateAccountService)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const inviteLinkVisible = ref(false)
const addAdminDialogVisible = ref<boolean>(false)
const currentRow = ref<InviteItem>({} as InviteItem)

const inviteLinkData = ref<InviteEmitData>({
  mobile: '',
  expire_time: 0,
  role_name: '',
})

const queryForm = reactive<InviteListReq>({
  page: 1,
  limit: 20,
  status: undefined,
  key_word: '',
})

const statusOptions = computed(() => {
  void locale.value
  return formatMapToOptions({
    [InviteState.pending]: t('inviteList.statePending'),
    [InviteState.allowed]: t('inviteList.stateAllowed'),
    [InviteState.rejected]: t('inviteList.stateRejected'),
    [InviteState.canceled]: t('inviteList.stateCanceled'),
    [InviteState.expire]: t('inviteList.stateExpire'),
  }, true)
})
const columns = computed<Partial<ObjectFormItem<InviteListReq, keyof InviteListReq>>>(() => {
  void locale.value
  return {
    status: {
      type: FormType.Select,
      colOpt: { style: 'flex: 0 0 150px;' },
      placeholder: t('inviteList.status'),
      dictData: statusOptions.value,
      hiddenAllLabel: true,
      selectOpt: { clearable: true },
      events: {
        change: (val) => {
          queryForm.status = val || undefined
          void refreshList()
        },
      },
    },
  }
})

const api = (params: InviteListReq) => {
  return inviteListService.getInviteList(params)
}

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: listCol(t, 'operation'), width: 160, align: 'left', fixed: 'right' as const },
    actionButtons: (row: InviteItem) => [
      {
        name: t('inviteList.copyLink'),
        type: InviteActionType.copyLink,
        opt: { size: FormSize.Small, text: true, type: 'text' },
        show: (r: InviteItem) => r.status === InviteState.pending,
      },
      {
        name: t('inviteList.cancelInvite'),
        type: InviteActionType.cancelInvite,
        opt: { size: FormSize.Small, text: true, type: 'text' },
        show: (r: InviteItem) => r.status === InviteState.pending,
      },
      {
        name: t('inviteList.reInvite'),
        type: InviteActionType.reInvite,
        opt: { size: FormSize.Small, text: true, type: 'text' },
        show: (r: InviteItem) => r.status === InviteState.rejected || r.status === InviteState.expire,
      },
      {
        name: '--',
        opt: { text: true, type: 'text', style: 'color: var(--cm-color-text-regular)!important; cursor: text;' },
        show: (r: InviteItem) => r.status === InviteState.allowed || r.status === InviteState.canceled,
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: 'padding: 15px;border-radius: var(--el-border-radius-large)',
    bodyClass: 'el-card',
    containerOpt: { style: 'background: var(--cm-color-card-bg)' },
    height: '100%',
    path: 'list',
    showActionBar: false,
    showForm: true,
    showSearch: true,
    showRightToolbar: false,
    rowKey: 'account',
    selection: false,
    columns: [
      {
        label: t('inviteList.inviteTime'),
        prop: 'ctime',
        formatter: (_row, val) => formatTimeStamp(val),
      },
      {
        label: t('inviteList.mobile'),
        formatter: (_row, val) => val || '--',
        prop: 'account',
      },
      {
        label: t('inviteList.adminRole'),
        formatter: (_row, val) => val || '--',
        prop: 'role_name',
      },
      {
        label: listCol(t, 'remark'),
        prop: 'remark',
        formatter: (_row, val) => val || '--',
        showOverflow: true,
      },
      {
        label: t('inviteList.status'),
        prop: 'status',
        formatter: (_row, val) => (val != null && inviteStateKeyMap[val as InviteState]) ? t('inviteList.' + inviteStateKeyMap[val as InviteState]) : '--',
      },
    ],
  }
})

function copyLink (row: InviteItem) {
  inviteLinkVisible.value = true
  inviteLinkData.value.mobile = row.account || ''
  inviteLinkData.value.expire_time = row.expire_time || 0
  inviteLinkData.value.role_name = row.role_name || ''
}

function cancelInvite (row: InviteItem) {
  ElMessageBox.confirm(t('inviteList.cancelInviteConfirm'), t('inviteList.tip')).then(() => {
    inviteListService.cancelInvite({
      account: row.account,
    }).then(() => {
      ElMessage.success(t('inviteList.cancelInviteSuccess'))
      void refreshList()
      emits('refresh')
    })
  })
}

function reInvite (row: InviteItem) {
  createAccountService.inviteAdmin({
    role_id: row.role_id,
    account: row.account,
    remark: row.remark,
  }).then((res) => {
    inviteLinkVisible.value = true
    void refreshList()
    inviteLinkData.value.mobile = row.account || ''
    inviteLinkData.value.expire_time = res.expire_time || 0
    inviteLinkData.value.role_name = row.role_name || ''
    nextTick(() => ElMessage.success(t('inviteList.reInviteSuccess')))
    emits('refresh')
  })
}

function handleCreateAccountSubmit({ invite_data }) {
  void refreshList()
  addAdminDialogVisible.value = false
  inviteLinkData.value = { ...invite_data }
  inviteLinkVisible.value = true
}

const actionHandlerMap: Record<InviteActionType, (row: InviteItem) => void> = {
  [InviteActionType.copyLink]: copyLink,
  [InviteActionType.cancelInvite]: cancelInvite,
  [InviteActionType.reInvite]: reInvite,
}

function handleAction ({ type, rowItem: row }: { type: string, rowItem?: InviteItem }) {
  if (!row || !type) return
  currentRow.value = row
  actionHandlerMap[type]?.(row)
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      tableOpt: tableOpt.value,
      modelValue: queryForm,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((queryForm) = $event)),
      tableActOpt: tableActOpt.value,
      api: api,
      ref_key: "tableRef",
      ref: tableRef,
      pageOption: { pageNumName: 'page', pageSizeName: 'limit' },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_el_button, {
            type: "primary",
            size: "small",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (addAdminDialogVisible.value = true))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('inviteList.addAdmin')), 1)
            ]),
            _: 1
          }),
          _createVNode(CmForm, {
            rowOpt: { style: 'justify-content:flex-end' },
            formOpt: { style: 'flex:auto' },
            formSize: _unref(FormSize).Small,
            modelValue: queryForm,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryForm) = $event)),
            formColumns: columns.value,
            formType: _unref(FormActionType).Other,
            "onUpdate:formType": _cache[2] || (_cache[2] = ($event: any) => ((_unref(FormActionType).Other) = $event))
          }, null, 8, ["formSize", "modelValue", "formColumns", "formType"])
        ])
      ]),
      _: 1
    }, 8, ["tableOpt", "modelValue", "tableActOpt"]),
    _createVNode(InviteLink, {
      modelValue: inviteLinkVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((inviteLinkVisible).value = $event)),
      mobile: inviteLinkData.value.mobile,
      "expire-time": inviteLinkData.value.expire_time,
      "role-name": inviteLinkData.value.role_name
    }, null, 8, ["modelValue", "mobile", "expire-time", "role-name"]),
    _createVNode(_component_el_dialog, {
      "append-to-body": "",
      title: _unref(t)('inviteList.newAdmin'),
      modelValue: addAdminDialogVisible.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((addAdminDialogVisible).value = $event)),
      width: "50%",
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(CreateAccount, {
          onClose: _cache[5] || (_cache[5] = ($event: any) => (addAdminDialogVisible.value = false)),
          onSubmit: handleCreateAccountSubmit
        })
      ]),
      _: 1
    }, 8, ["title", "modelValue"])
  ], 64))
}
}

})